import { FileKey2 } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api, isApiError } from '../../api';
import type { IntegrationEnvironment, ProviderSettingDetail } from '../../api/areas/integrations';
import type { WhoisImportResult } from '../../api/areas/lookup';
import { Callout, Field } from '../../components/blocks';
import { KeyValue } from '../../components/KeyValue';
import { CopyText } from '../../components/States';
import { formatDateTime } from '../../lib/format';
import { newIdempotencyKey } from '../../lib/ids';
import { ChangeDialog, ConfirmCheck, EnvironmentBanner, IntegrationError, Panel, ReasonField, WriteOutcome } from './shared';

const LIMIT = 64 * 1024;

async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

type State = { phase: 'idle' } | { phase: 'busy' } | { phase: 'done'; result: WhoisImportResult } | { phase: 'error'; error: unknown };

/**
 * Cài đặt › Kết nối › VNNIC › WHOIS: the dedicated WHOIS client certificate, imported from a
 * PKCS#12 file into a new VNNIC_WHOIS version (`POST /provider-settings/VNNIC_WHOIS/certificates/pkcs12`).
 */
export function WhoisCertificatePanel({ env, detail, onDone }: { env: IntegrationEnvironment; detail: ProviderSettingDetail; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const [open, setOpen] = useState(false);
  const pendingOpen = !!detail.pending;
  return (
    <Panel
      title={t('whoisImport.title')}
      description={t('whoisImport.description')}
      actions={
        <Button size="sm" onClick={() => setOpen(true)} disabled={pendingOpen} title={pendingOpen ? t('envTab.blocked') : undefined}>
          <FileKey2 /> {t('whoisImport.open')}
        </Button>
      }
    >
      <p className="text-xs text-muted-foreground">{t('whoisImport.tls')}</p>
      <ChangeDialog open={open} onOpenChange={setOpen} title={t('whoisImport.dialogTitle')} description={t('whoisImport.description')} wide>
        <ImportForm env={env} detail={detail} onClose={() => setOpen(false)} onDone={onDone} />
      </ChangeDialog>
    </Panel>
  );
}

function ImportForm({ env, detail, onClose, onDone }: { env: IntegrationEnvironment; detail: ProviderSettingDetail; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const fileId = useId();
  const [file, setFile] = useState<File | null>(null);
  const [passphrase, setPassphrase] = useState('');
  const [ca, setCa] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [state, setState] = useState<State>({ phase: 'idle' });
  const attempt = useRef<{ key: string; file: File; reason: string; unanswered: boolean } | null>(null);
  const tooLarge = !!file && file.size > LIMIT;
  const valid = !!file && !tooLarge && file.size > 0 && passphrase.length > 0 && reason.trim().length > 0 && confirmed;

  const submit = async () => {
    if (!file) return;
    const why = reason.trim();
    const prev = attempt.current;
    const key = prev && prev.unanswered && prev.file === file && prev.reason === why ? prev.key : newIdempotencyKey();
    attempt.current = { key, file, reason: why, unanswered: true };
    setState({ phase: 'busy' });
    // The passphrase leaves component state as soon as it is sent.
    const secret = passphrase;
    setPassphrase('');
    try {
      const result = await api.importWhoisPkcs12(
        {
          expected_generation: detail.configured_in_database ? (detail.generation ?? null) : null,
          base_url: baseUrl.trim() || null,
          file_base64: await fileToBase64(file),
          passphrase: secret,
          ca_certificate_pem: ca.trim() || null,
          reason: why,
        },
        { idempotencyKey: key },
      );
      attempt.current = null;
      setFile(null);
      setState({ phase: 'done', result });
      onDone();
    } catch (error) {
      if (attempt.current && isApiError(error)) attempt.current.unanswered = false;
      setState({ phase: 'error', error });
    }
  };

  if (state.phase === 'done') {
    const r = state.result;
    const c = r.certificate;
    return (
      <div className="space-y-4">
        <EnvironmentBanner env={env} />
        <WriteOutcome result={r.result} approval={r.approval} warnings={r.warnings} env={env} appliedText={t('whoisImport.done', { subject: c.subject, until: formatDateTime(c.not_after) })} />
        {r.result !== 'APPLIED' ? <Callout tone="info">{t('whoisImport.pending')}</Callout> : null}
        <KeyValue
          columns={2}
          rows={[
            [t('certificates.subject'), <span key="s" className="[overflow-wrap:anywhere]">{c.subject}</span>],
            [t('certificates.issuer'), <span key="i" className="[overflow-wrap:anywhere]">{c.issuer}</span>],
            [t('certificates.validity'), `${formatDateTime(c.not_before)} → ${formatDateTime(c.not_after)}`],
            [t('certificates.fingerprint'), <CopyText key="f" value={c.fingerprint_sha256} display={`${c.fingerprint_sha256.slice(0, 16)}…`} />],
          ]}
        />
        <DialogFooter>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogFooter>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} />
      <div className="grid gap-1.5 text-sm">
        <span className="text-xs font-medium text-muted-foreground">{t('whoisImport.file')}</span>
        <div className="flex flex-wrap items-center gap-2">
          <input
            id={fileId}
            type="file"
            accept=".p12,.pfx,application/x-pkcs12"
            className="sr-only"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
          <Button type="button" variant="outline" size="sm" asChild>
            <label htmlFor={fileId} className="cursor-pointer">
              <FileKey2 /> {file ? t('pkcs12.chooseOther') : t('pkcs12.choose')}
            </label>
          </Button>
          {file ? <span className="min-w-0 text-sm break-all">{file.name}</span> : <span className="text-sm text-muted-foreground">{t('pkcs12.noFile')}</span>}
        </div>
        {tooLarge ? <span className="text-xs text-critical">{t('pkcs12.tooLarge')}</span> : null}
      </div>
      <Field label={t('whoisImport.passphrase')} hint={t('whoisImport.passphraseHint')}>
        <Input type="password" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} autoComplete="off" spellCheck={false} />
      </Field>
      <Field label={t('whoisImport.ca')} hint={t('whoisImport.caHint')}>
        <Textarea value={ca} onChange={(e) => setCa(e.target.value)} rows={3} spellCheck={false} className="font-mono text-xs" placeholder="-----BEGIN CERTIFICATE-----" />
      </Field>
      <Field label={t('whoisImport.baseUrl')} hint={t('whoisImport.baseUrlHint')}>
        <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} spellCheck={false} placeholder="https://" />
      </Field>
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} env={env} />
      {state.phase === 'error' ? <IntegrationError error={state.error} /> : null}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button onClick={() => void submit()} disabled={!valid || state.phase === 'busy'}>
          <FileKey2 /> {env === 'PRODUCTION' ? t('form.submitForApproval') : t('whoisImport.submit')}
        </Button>
      </DialogFooter>
    </div>
  );
}
